/**
 * The cloud deck: four parallax plates, and the one rule that turns layers into
 * distance.
 *
 * THE PLATES ARE MASKS, NOT PHOTOGRAPHS. Each file is a single channel
 * luminance plate consumed with mask-mode, filled with --cloud-ink. A
 * photograph of a cloud carries a colour temperature and a sun position and
 * both of them fight a direction flip; a mask has neither and weighs a third as
 * much. It also means the whole deck restyles from one token.
 *
 * DEPTH IS ONE NUMBER. Every layer carries --layer-depth from 0 (far) to 1
 * (near) and that single value drives opacity, blur and the mix toward the page
 * ground. Far clouds are not merely smaller and slower, they are lifted toward
 * the background value, which is the thing that converts three sprites at three
 * speeds into actual distance. Without it a reader can count the layers.
 *
 * WIND DOES NOT START UNTIL THE HERO HAS FINISHED TALKING. HeroBackdrop wrote
 * the rule: one thing moves per screen, and while the address bar is typing
 * that one thing is the address bar. So the deck mounts frozen and the caller
 * flips it on at the same instant the founder loop starts playing.
 *
 * THE FOURTH LAYER IS NOT IN HERE. Vapor is chapter scoped: it makes exactly
 * two passes, one as the second statement arrives and one as the corridor
 * opens, and each pass is driven by its own chapter's view progress rather than
 * by document scroll. It is exported below as VaporPass and mounted by the
 * chapter that wants it.
 *
 * It is decoration. aria-hidden, no focus stop.
 */

import { CLOUD_LAYERS } from '../../screens/marketing/streamManifest';

import '../../styles/components/cloud-field.css';

/** The three deck plates, far to near. Vapor is the fourth and it is chapter scoped. */
const DECK = ['far', 'mid', 'near'] as const;

export interface CloudFieldProps {
  /** Wind is off until the hero sequence lands. See the note above. */
  wind?: boolean;
}

export function CloudField({ wind = false }: CloudFieldProps) {
  return (
    <div className="cloudfield" data-wind={wind ? 'on' : 'off'} aria-hidden="true">
      {/* L0. No asset at all: a radial lift of the page ground, which is what
          keeps the horizon from being a hard line where the plates stop. */}
      <span className="cloudfield__haze" />

      {DECK.map((name, index) => {
        const layer = CLOUD_LAYERS[index];
        if (!layer) return null;
        return (
          <span
            key={name}
            className="cloudfield__layer"
            data-layer={name}
            style={{ '--layer-depth': layer.depth } as React.CSSProperties}
          >
            {/* The plate is twice the layer wide and its mask tiles at exactly
                the layer width, so the wind can translate it by one whole tile
                and land back where it started. A wind that animates
                mask-position instead would be correct and would also repaint
                every frame on the main thread. */}
            <span
              className="cloudfield__plate"
              style={{ '--plate': `url(${layer.src})` } as React.CSSProperties}
            />
          </span>
        );
      })}
    </div>
  );
}

export interface VaporPassProps {
  /**
   * Which pass this is. The two differ in amplitude and in direction, because
   * they are doing different jobs: the climb pass crosses the frame as the
   * reader rises through the deck, and the corridor pass wipes the frame open
   * as the flight begins.
   */
  pass: 'climb' | 'wipe';
}

/**
 * L4. It travels further than the page content does, which is the whole reason
 * it reads as passing the camera rather than as scrolling with it.
 *
 * Its timeline is its own chapter's view progress, so it sweeps exactly when
 * that chapter is being read and it never runs at any other time. On a browser
 * with no scroll timelines, and under reduced motion, it renders at the one
 * authored position the storyboard names and never moves.
 */
export function VaporPass({ pass }: VaporPassProps) {
  const layer = CLOUD_LAYERS[3];
  if (!layer) return null;
  return (
    <span className="vapor" data-pass={pass} aria-hidden="true">
      <span className="vapor__plate" style={{ '--plate': `url(${layer.src})` } as React.CSSProperties} />
    </span>
  );
}

export default CloudField;
